/**
 * Dynamic Radar Chart (Net Diagram / Spider Chart) Component
 * Displays skill profiling based on quiz performance across dynamic topics.
 *
 * Accepts topics as props (array of { id, name, color }) instead of hardcoded categories.
 * Shows two overlapping polygons:
 * - Blue: Topic relevance / baseline (possible)
 * - Orange: User's actual performance (achieved)
 */

import React, { useMemo } from 'react';

// Default color palette for topics that don't specify one
const DEFAULT_COLORS = [
  '#1890ff', '#52c41a', '#722ed1', '#faad14', '#13c2c2', '#eb2f96',
  '#fa541c', '#2f54eb', '#a0d911', '#fa8c16', '#9254de', '#f5222d',
];

// Chart configuration
const CONFIG = {
  size: 320,
  centerX: 160,
  centerY: 160,
  maxRadius: 120,
  levels: 5,
  labelOffset: 28,
  dotRadius: 5,
  colors: {
    baseline: {
      fill: 'rgba(59, 130, 246, 0.2)',
      stroke: '#3b82f6',
      dot: '#2563eb',
    },
    achieved: {
      fill: 'rgba(249, 115, 22, 0.35)',
      stroke: '#f97316',
      dot: '#ea580c',
    },
    grid: {
      line: '#e5e7eb',
      axis: '#9ca3af',
      label: '#374151',
    },
  },
};

const getPointPosition = (index, total, value, maxValue, centerX, centerY, maxRadius) => {
  const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
  const radius = (value / maxValue) * maxRadius;
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

const generatePolygonPath = (points) => {
  if (points.length === 0) return '';
  const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return `${pathData} Z`;
};

/** Concentric grid circles */
const GridLevels = ({ levels, centerX, centerY, maxRadius }) => {
  const gridLevels = [];
  for (let level = 1; level <= levels; level++) {
    const radius = (maxRadius / levels) * level;
    gridLevels.push(
      <circle
        key={`grid-${level}`}
        cx={centerX}
        cy={centerY}
        r={radius}
        fill="none"
        stroke={CONFIG.colors.grid.line}
        strokeWidth="1"
        strokeDasharray={level === levels ? 'none' : '3,3'}
        opacity={level === levels ? 0.8 : 0.5}
      />
    );
  }
  return <g className="grid-levels">{gridLevels}</g>;
};

/** Radial axis lines with clickable topic labels */
const AxisLines = ({ topics, centerX, centerY, maxRadius, labelOffset, onTopicClick }) => {
  const total = topics.length;
  return (
    <g className="axis-lines">
      {topics.map((topic, index) => {
        const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
        const endX = centerX + maxRadius * Math.cos(angle);
        const endY = centerY + maxRadius * Math.sin(angle);
        const labelX = centerX + (maxRadius + labelOffset) * Math.cos(angle);
        const labelY = centerY + (maxRadius + labelOffset) * Math.sin(angle);
        const isClickable = !!onTopicClick;
        const color = topic.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

        return (
          <g key={`axis-${topic.id}`}>
            <line
              x1={centerX} y1={centerY} x2={endX} y2={endY}
              stroke={CONFIG.colors.grid.axis} strokeWidth="1" opacity="0.6"
            />
            <text
              x={labelX} y={labelY}
              textAnchor="middle" dominantBaseline="middle"
              fill={isClickable ? color : CONFIG.colors.grid.label}
              fontSize="11"
              fontWeight={isClickable ? '600' : '500'}
              style={{
                userSelect: 'none',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'fill 0.2s, transform 0.2s',
              }}
              onClick={isClickable ? () => onTopicClick(topic.id) : undefined}
              onMouseEnter={isClickable ? (e) => { e.target.style.fill = color; e.target.style.fontSize = '12px'; } : undefined}
              onMouseLeave={isClickable ? (e) => { e.target.style.fill = color; e.target.style.fontSize = '11px'; } : undefined}
            >
              {topic.name.length > 14 ? topic.name.slice(0, 12) + '…' : topic.name}
            </text>
          </g>
        );
      })}
    </g>
  );
};

/** Filled data polygon */
const DataPolygon = ({ data, topics, centerX, centerY, maxRadius, colors, showDots = true }) => {
  const total = topics.length;
  const points = topics.map((topic, index) => {
    const value = data[topic.id] || 0;
    return getPointPosition(index, total, value, 100, centerX, centerY, maxRadius);
  });
  const path = generatePolygonPath(points);

  return (
    <g className="data-polygon">
      <path d={path} fill={colors.fill} stroke={colors.stroke} strokeWidth="2.5" strokeLinejoin="round" />
      {showDots && points.map((point, index) => (
        <circle
          key={`dot-${index}`}
          cx={point.x} cy={point.y} r={CONFIG.dotRadius}
          fill={colors.dot} stroke="#fff" strokeWidth="1.5"
        />
      ))}
    </g>
  );
};

/** Percentage labels at grid levels */
const PercentageLabels = ({ levels, centerX, centerY, maxRadius }) => {
  const labels = [];
  for (let level = 1; level <= levels; level++) {
    const radius = (maxRadius / levels) * level;
    const percentage = (100 / levels) * level;
    labels.push(
      <text key={`pct-${level}`} x={centerX + 4} y={centerY - radius + 4}
        fontSize="9" fill="#9ca3af" style={{ userSelect: 'none' }}>
        {percentage}%
      </text>
    );
  }
  return <g className="percentage-labels">{labels}</g>;
};

/** Legend */
const Legend = ({ showBaseline = true, baselineLabel = 'Topic Coverage', achievedLabel = 'Your Performance' }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '16px', fontSize: '13px' }}>
    {showBaseline && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '16px', height: '16px',
          backgroundColor: CONFIG.colors.baseline.fill,
          border: `2px solid ${CONFIG.colors.baseline.stroke}`,
          borderRadius: '3px',
        }} />
        <span style={{ color: '#6b7280' }}>{baselineLabel}</span>
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{
        width: '16px', height: '16px',
        backgroundColor: CONFIG.colors.achieved.fill,
        border: `2px solid ${CONFIG.colors.achieved.stroke}`,
        borderRadius: '3px',
      }} />
      <span style={{ color: '#6b7280' }}>{achievedLabel}</span>
    </div>
  </div>
);

/** Topic details sidebar */
const TopicDetails = ({ topics, baselineData, achievedData, onTopicClick }) => {
  const isClickable = !!onTopicClick;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '6px',
      padding: '8px', background: '#f9fafb', borderRadius: '8px',
      minWidth: '180px', marginLeft: '24px',
    }}>
      {topics.map((topic, i) => {
        const color = topic.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
        const baseline = baselineData[topic.id] || 0;
        const achieved = achievedData[topic.id] || 0;

        return (
          <div
            key={topic.id}
            onClick={isClickable ? () => onTopicClick(topic.id) : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 8px', background: '#fff', borderRadius: '6px',
              border: '1px solid #e5e7eb',
              cursor: isClickable ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {topic.name}
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>
                <span style={{ color: CONFIG.colors.achieved.stroke, fontWeight: '600' }}>{achieved}%</span>
                {' / '}
                <span style={{ color: CONFIG.colors.baseline.stroke }}>{baseline}%</span>
                <span style={{ color: '#9ca3af', marginLeft: '2px' }}>max</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Main RadarChart Component
 *
 * @param {Array<{id: number|string, name: string, color?: string}>} props.topics - Dynamic topic list
 * @param {Object} props.baselineData - Keyed by topic id → 0-100 percentage
 * @param {Object} props.achievedData - Keyed by topic id → 0-100 percentage
 * @param {boolean} props.showLegend
 * @param {boolean} props.showDetails
 * @param {string} props.title
 * @param {Function} props.onTopicClick
 * @param {string} props.baselineLabel
 * @param {string} props.achievedLabel
 */
const RadarChart = ({
  topics = [],
  baselineData,
  achievedData,
  showLegend = true,
  showDetails = true,
  title,
  onTopicClick,
  baselineLabel,
  achievedLabel,
}) => {
  const { size, centerX, centerY, maxRadius, levels, labelOffset } = CONFIG;

  // Enrich topics with default colors
  const enrichedTopics = useMemo(() =>
    topics.map((t, i) => ({ ...t, color: t.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length] })),
    [topics]
  );

  const safeBaselineData = useMemo(() => {
    const result = {};
    enrichedTopics.forEach(t => { result[t.id] = baselineData?.[t.id] || 0; });
    return result;
  }, [enrichedTopics, baselineData]);

  const safeAchievedData = useMemo(() => {
    const result = {};
    enrichedTopics.forEach(t => { result[t.id] = achievedData?.[t.id] || 0; });
    return result;
  }, [enrichedTopics, achievedData]);

  if (enrichedTopics.length < 3) return null; // Need at least 3 topics for a radar chart

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
    }}>
      {title && (
        <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
          {title}
        </h4>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
          <GridLevels levels={levels} centerX={centerX} centerY={centerY} maxRadius={maxRadius} />
          <AxisLines topics={enrichedTopics} centerX={centerX} centerY={centerY} maxRadius={maxRadius} labelOffset={labelOffset} onTopicClick={onTopicClick} />
          <PercentageLabels levels={levels} centerX={centerX} centerY={centerY} maxRadius={maxRadius} />
          <DataPolygon data={safeBaselineData} topics={enrichedTopics} centerX={centerX} centerY={centerY} maxRadius={maxRadius} colors={CONFIG.colors.baseline} showDots={false} />
          <DataPolygon data={safeAchievedData} topics={enrichedTopics} centerX={centerX} centerY={centerY} maxRadius={maxRadius} colors={CONFIG.colors.achieved} showDots={true} />
        </svg>
        {showDetails && (
          <TopicDetails topics={enrichedTopics} baselineData={safeBaselineData} achievedData={safeAchievedData} onTopicClick={onTopicClick} />
        )}
      </div>

      {showLegend && <Legend showBaseline={true} baselineLabel={baselineLabel} achievedLabel={achievedLabel} />}
    </div>
  );
};

export default RadarChart;
